import { applyOps, assetsMap, AssetSchema, ROOT_ID, type ImagePaint } from '@flobi/artwork-core';
import { LOCAL_ORIGIN, type OpenDocument } from '../useEditorDocument';
import { behindLooseArt, besideBoards } from '../tools/artboard';
import type { Result } from '../ai/contracts';
import { pictureLayer, type PictureLayer } from './run';

/*
 * Where an image tool's result goes on the page. Every landing is one undo step and leaves the
 * source picture recoverable: a replaced picture is an Undo away (and on a shape filled by
 * Generate, one of its takes), and a picture split into layers or traced into vectors is hidden,
 * never deleted. A "picture" is an image layer or a shape filled with one (`pictureLayer`).
 */

type Doc = Pick<OpenDocument, 'root' | 'page' | 'scene' | 'undo'>;

function register(open: Doc, result: Result, kind: 'image' | 'video') {
  const asset = AssetSchema.parse({ kind, name: result.name, mediaAssetId: result.mediaAssetId, driveFileId: result.driveFileId, src: result.url, mime: result.mime,
    width: result.width, height: result.height, ...(result.duration ? { duration: result.duration } : {}) });
  open.root.transact(() => assetsMap(open.root).set(result.mediaAssetId, asset), LOCAL_ORIGIN);
}

function step(open: Doc, ops: unknown[]): Record<string, string | undefined> {
  open.undo.stopCapturing();
  const outcome = applyOps(open.page, ops as never, { scene: open.scene, origin: LOCAL_ORIGIN });
  open.undo.stopCapturing();
  if (!outcome.ok) { console.warn('Could not place the result', outcome.errors); throw Error('The result could not be placed. It is saved in My Stuff.'); }
  return outcome.created;
}

function pictureOf(open: Doc, id: string): PictureLayer {
  const node = pictureLayer(open, id);
  if (!node) throw Error('The picture this was made from is no longer on the page. The result is saved in My Stuff.');
  if (open.scene.lockedHere(id)) throw Error('The picture is locked. The result is saved in My Stuff.');
  return node;
}

/** The frame grown about its middle to a new shape, on the side that gained picture (Expand). */
function grown(node: PictureLayer, ratio: number): Record<string, unknown> {
  const frameRatio = node.width / node.height;
  if (!(ratio > 0) || Math.abs(frameRatio - ratio) <= 0.01 * frameRatio) return {};
  const width = ratio > frameRatio ? node.height * ratio : node.width;
  const height = ratio > frameRatio ? node.height : node.width / ratio;
  const t = node.transform, dx = (width - node.width) / 2, dy = (height - node.height) / 2;
  return { width, height, transform: [t[0], t[1], t[2], t[3], t[4]! - (t[0]! * dx + t[2]! * dy), t[5]! - (t[1]! * dx + t[3]! * dy)] };
}

const plainFit = (node: PictureLayer) => node.fit === 'fit' ? 'fit' : 'crop';

/**
 * The layer shows the result instead (Remove Background, Upscale, Erase, Edit, Retouch, New
 * Background). A shape filled by Generate keeps its takes: the result joins them and is shown, so
 * the one before is a press away. With `grow` (Expand) the frame grows to the result's shape.
 */
export function replacePicture(open: Doc, id: string, result: Result, grow = false, flattened = false) {
  const node = pictureOf(open, id);
  register(open, result, 'image');
  const shape = grow && result.width > 0 && result.height > 0 ? grown(node, result.width / result.height) : {};
  if (flattened) { step(open, [{ op: 'set', id, props: { ...flattenedProps(open, id, node, result), ...shape } }]); return; }
  if (node.fill === null) {
    step(open, [{ op: 'set', id, props: { assetId: result.mediaAssetId, ...(node.clip ? { clip: null } : {}), ...shape, ...(Object.keys(shape).length ? { fit: 'crop' } : {}) } }]);
    return;
  }
  const fills = [...((open.scene.get(id) as unknown as { fills: ImagePaint[] }).fills)];
  const paint = fills[node.fill]!;
  const set = paint.resultSet;
  if (set) {
    const assetIds = [...set.assetIds.filter(item => item !== result.mediaAssetId), result.mediaAssetId].slice(-32);
    fills[node.fill] = { ...paint, assetId: result.mediaAssetId, resultSet: { ...set, assetIds, active: assetIds.length - 1 } };
  } else fills[node.fill] = { ...paint, assetId: result.mediaAssetId };
  step(open, [{ op: 'set', id, props: { fills, ...shape } }]);
}

/**
 * A result made from the layer as it looked: it already wears the layer's effects, extra fills and
 * stroke, so it becomes the layer's one picture, filling its frame, and those are switched off
 * (kept, not deleted: turning one back on puts it on top again). Undo brings the stack back.
 */
function flattenedProps(open: Doc, id: string, node: PictureLayer, result: Result): Record<string, unknown> {
  type Item = { visible?: boolean } & Record<string, unknown>;
  const raw = open.scene.get(id) as unknown as { crop?: object; effects?: Item[]; strokes?: Item[]; fills?: Item[] };
  const off = (list: Item[] | undefined) => (list ?? []).map(item => ({ ...item, visible: false }));
  const props: Record<string, unknown> = {};
  if (raw.effects?.length) props.effects = off(raw.effects);
  if (raw.strokes?.length) props.strokes = off(raw.strokes);
  if (node.fill === null) return { ...props, assetId: result.mediaAssetId, fit: 'crop', ...(raw.crop ? { crop: null } : {}), ...(raw.fills?.length ? { fills: off(raw.fills) } : {}) };
  const fills = (raw.fills ?? []).map((paint, index) => {
    if (index !== node.fill) return { ...paint, visible: false };
    const picture = paint as unknown as ImagePaint, set = picture.resultSet;
    const assetIds = set ? [...set.assetIds.filter(item => item !== result.mediaAssetId), result.mediaAssetId].slice(-32) : null;
    const { transform: _transform, crop: _crop, ...rest } = picture;
    return { ...rest, assetId: result.mediaAssetId, fit: 'crop', scale: 1, opacity: 1, blendMode: 'normal', visible: true, ...(set && assetIds ? { resultSet: { ...set, assetIds, active: assetIds.length - 1 } } : {}) };
  });
  return { ...props, fills };
}

/** A clip tool's result (Remove Background, Upscale, Edit on a clip): the clip plays the new one. */
export function replaceClip(open: Doc, id: string, result: Result) {
  const node = pictureOf(open, id);
  register(open, result, 'video');
  step(open, [{ op: 'set', id, props: { assetId: result.mediaAssetId, clip: { duration: result.duration || node.clip?.duration || 5 } } }]);
}

/** Layers (Split into Layers, Cut Out): a group of pictures over the source, which is hidden when they replace it. */
export function addLayers(open: Doc, id: string, results: Result[], name: string, hideSource: boolean): string | null {
  const node = pictureOf(open, id);
  for (const result of results) register(open, result, 'image');
  const ops: unknown[] = [
    { op: 'create', ref: 'layers', parentId: open.scene.parentOf(id) ?? 'root', position: { after: id }, node: { type: 'group', name, transform: [...node.transform] } },
    // Bottom first: a split answers back-to-front, the background first.
    ...results.map((result, index) => ({ op: 'create', ref: `layer${index}`, parentId: '$layers', position: 'front',
      node: { type: 'image', name: results.length === 1 ? name : layerName(index, results.length), assetId: result.mediaAssetId, width: node.width, height: node.height, fit: plainFit(node), transform: [1, 0, 0, 1, 0, 0] } })),
    ...(hideSource ? [{ op: 'set', id, props: { visible: false } }] : []),
  ];
  return step(open, ops).layers ?? null;
}

/** A split's layers by place, never by file ("standalone-50a6…"): the bottom one is the background. */
const layerName = (index: number, count: number) => (count > 1 && index === 0 ? 'Background' : `Layer ${count > 1 ? index : index + 1}`);

/** White paper, as a new page is. */
export const PAPER = { type: 'solid', color: { r: 1, g: 1, b: 1, a: 1 }, visible: true, opacity: 1, blendMode: 'normal' };

/**
 * Split into Layers: the layers on an artboard of their own, beside the others, the size of the
 * picture they were made from (Tarek, 15 Oct: "create an artboard for the content instead of a
 * group", then "make the split artboard the same size as the original picture") — each layer
 * fills it, whatever size the model answered at. The picture itself stays where it is, as it was.
 */
export function addLayersBoard(open: Doc, id: string, results: Result[], name: string, size?: { width: number; height: number } | null): string | null {
  const node = pictureLayer(open, id);
  for (const result of results) register(open, result, 'image');
  const first = results[0]!;
  const width = Math.round(size?.width || first.width || node?.width || 1024), height = Math.round(size?.height || first.height || node?.height || 1024);
  const { x, y } = besideBoards(open.scene);
  return step(open, [
    { op: 'create', ref: 'board', parentId: ROOT_ID, ...behindLooseArt(open.scene), node: { type: 'artboard', name, width, height, fills: [PAPER], transform: [1, 0, 0, 1, Math.round(x), Math.round(y)] } },
    // Bottom first: a split answers back-to-front, the background first.
    ...results.map((result, index) => ({ op: 'create', ref: `layer${index}`, parentId: '$board', position: 'front',
      node: { type: 'image', name: layerName(index, results.length), assetId: result.mediaAssetId, width, height, fit: 'crop', transform: [1, 0, 0, 1, 0, 0] } })),
  ]).board ?? null;
}

/** A clip made from the picture: beside it, the same height, so both are seen. */
export function addClipBeside(open: Doc, id: string, result: Result): string | null {
  const node = pictureOf(open, id);
  register(open, result, 'video');
  const ratio = result.width > 0 && result.height > 0 ? result.width / result.height : node.width / node.height;
  const height = node.height, width = height * ratio, gap = Math.max(16, Math.round(node.width * 0.06));
  const t = node.transform, shift = node.width + gap;
  return step(open, [{ op: 'create', ref: 'clip', parentId: open.scene.parentOf(id) ?? 'root', position: { after: id },
    node: { type: 'image', name: result.name || `${node.name || 'Picture'} clip`, assetId: result.mediaAssetId, width, height, fit: 'crop', clip: { duration: result.duration || 5 },
      transform: [t[0], t[1], t[2], t[3], t[4]! + t[0]! * shift, t[5]! + t[1]! * shift] } }]).clip ?? null;
}
